
import { FC } from "react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";

interface MessageContentProps {
  text: string;
  isHtml?: boolean;
  isMarkdown?: boolean;
}

const MessageContent: FC<MessageContentProps> = ({ text, isHtml = false, isMarkdown = false }) => {
  if (isHtml) {
    // Sanitize HTML content to prevent XSS attacks
    const sanitizedHtml = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ADD_ATTR: ['target', 'rel']
    });
    
    return (
      <div 
        className="text-sm break-words"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }} 
      />
    );
  }

  if (isMarkdown) {
    console.log("Rendering markdown content:", text.substring(0, 100) + "...");
    return (
      <div className="text-sm break-words">
        <ReactMarkdown
          components={{
            p: ({ children }) => <p className="mb-2 last:mb-0 text-[#111418]">{children}</p>,
            strong: ({ children }) => <strong className="font-bold text-[#111418]">{children}</strong>,
            em: ({ children }) => <em className="italic text-[#111418]">{children}</em>,
            ul: ({ children }) => <ul className="list-disc list-inside ml-4 mb-2 text-[#111418]">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside ml-4 mb-2 text-[#111418]">{children}</ol>,
            li: ({ children }) => <li className="mb-1 text-[#111418]">{children}</li>,
            h1: ({ children }) => <h1 className="text-lg font-bold mb-2 text-[#111418]">{children}</h1>,
            h2: ({ children }) => <h2 className="text-base font-bold mb-2 text-[#111418]">{children}</h2>,
            h3: ({ children }) => <h3 className="text-sm font-bold mb-1 text-[#111418]">{children}</h3>,
            hr: () => <hr className="my-3 border-gray-300" />,
            a: ({ children, href }) => (
              <a 
                href={href} 
                className="text-blue-600 hover:underline font-medium" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
          }}
        >
          {text}
        </ReactMarkdown>
      </div>
    );
  }
  
  return <div className="text-sm break-words">{text}</div>;
};

export default MessageContent;
